import { InputHTMLAttributes, forwardRef } from 'react';

import clsx from 'clsx';

import { Label } from './label';
import { Fieldset } from './fieldset';

type InputCheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  classNameFieldSet?: string;
  label?: string;
  name: string;
  errorMessage?: string;
};

/**
 * InputCheckbox, consists of a fieldset with label and an input field
 * forwardRef is used to pass the ref to the input field, since we are using ReactFormHook
 * @props:
 * extends InputHTMLAttributes<HTMLInputElement>
 * - label: the label of the input field
 * - classNameFieldSet: the class of the fieldset
 * - name: the name of the input field (required)
 */
export const InputCheckbox = forwardRef<HTMLInputElement, InputCheckboxProps>((props, ref) => {
  const {
    id,
    name,
    label,
    required,
    classNameFieldSet = '',
    className = '',
    errorMessage,
    ...rest
  } = props;

  return (
    <Fieldset className={classNameFieldSet}>
      <div className="flex items-center space-x-2">
        <input
          {...rest}
          ref={ref}
          className={clsx(
            'peer border-input dark:bg-input/30 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground dark:data-[state=checked]:bg-primary data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          id={id ?? name}
          type="checkbox"
          name={name}
          required={required}
        />
        {label && (
          <Label htmlFor={id ?? name} required={required}>
            {label}
          </Label>
        )}
      </div>

      {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
    </Fieldset>
  );
});

InputCheckbox.displayName = 'InputCheckbox';
